import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'

import { cn } from '../lib/cn'
import { Spin } from '../spin/Spin'

const linkCommonCss =
  'text-primary underline-offset-4 hover:!underline hover:text-secondary-foreground border-0 font-semibold'
const ghostCommonCss = 'hover:bg-primary-foreground hover:text-primary border-0'

const buttonVariants = cva(
  'relative inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-lg text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-5 [&_svg]:shrink-0 border border-foreground/40',
  {
    variants: {
      variant: {
        default:
          'bg-card text-foreground hover:bg-primary-foreground dark:hover:bg-background hover:text-primary hover:border-primary/50',
        destructive:
          'bg-destructive/50 text-destructive-foreground border-destructive-foreground/50 hover:bg-destructive-foreground dark:hover:bg-background-foreground hover:text-destructive  hover:border-destructive-foreground dark:hover:border-destructive',
        outline: 'border bg-card hover:bg-accent hover:text-accent-foreground',
        outlineMuted: 'border-2 border-muted bg-card hover:text-primary hover:bg-card',
        primary:
          'bg-primary text-primary-foreground hover:bg-primary-foreground dark:hover:bg-background hover:text-primary border-primary/50',
        secondary: 'bg-foreground text-background hover:bg-card hover:text-foreground border-foreground/50',
        ghost: ghostCommonCss,
        ghostOutline: `${ghostCommonCss} border hover:border-primary/50`,
        link: linkCommonCss,
        linkNoPadding: `${linkCommonCss} !p-0`,
      },
      size: {
        default: 'h-9.5 px-4 py-2',
        sm: 'h-7 rounded-lg px-2 text-xs [&_svg]:!size-4',
        lg: 'h-10 rounded-lg px-8 [&_svg]:!size-5',
        icon: 'h-9 w-9',
        iconCircle: 'h-9 w-9 rounded-full',
        block: 'h-9.5 w-full',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  classNameText?: string
  asChild?: boolean
  isLoading?: boolean
}
const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, classNameText, variant, size, asChild = false, isLoading = false, children, ...props }, ref) => {
    const classes = cn(buttonVariants({ variant, size, className }))

    // isLoading is not supported with asChild.
    if (asChild) {
      return (
        <Slot className={classes} ref={ref} {...props}>
          {children}
        </Slot>
      )
    }

    return (
      <button type='button' className={classes} ref={ref} {...props}>
        {isLoading ? <Spin size='sm' className='absolute' /> : null}
        <span className={cn('flex items-center justify-center gap-2 w-full', classNameText, isLoading && 'invisible')}>
          {children}
        </span>
      </button>
    )
  },
)
Button.displayName = 'Button'

export { Button, buttonVariants }
